import {
    Avatar,
    Box,
    Chip,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getPrimaryRole } from '@src/App/Helper/roleHelper';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import { Building2, Calendar, Globe, Linkedin, Mail, MoreVertical, Sparkles } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { EmployeePicture } from '../../../General/EmployeePicture/EmployeePicture';

interface ProfileHeaderProps {
    employee: Employee;
    itsMe: boolean;
    orgUnit?: { id: number; title: string } | undefined;
    isTeamLead?: boolean;
    isMobile: boolean;
    showOverviewTiles?: boolean;
}

function ProfileHeader({
    employee,
    itsMe,
    orgUnit,
    isTeamLead = false,
    isMobile,
    showOverviewTiles = false,
}: ProfileHeaderProps) {
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const OUs = useSelector((state: AppState) => state.OUs);
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    // Find OU from store if not provided, same as in ProfileAvatar
    const employeeOU = orgUnit || (OUs ? OUs.find((o) => o.employees.includes(employee.id)) : undefined);

    // Get primary role using central helper
    const primaryRole = getPrimaryRole(employee.roles || []);

    // Get Focus Power (first SuperPower based on order)
    const focusPower = employee.superPowerOrder?.length
        ? employee.skills?.find((skill) => skill.id === employee.superPowerOrder?.[0])
        : employee.skills?.find((skill) => skill.isSuperPower === true);

    // Debug logging
    console.log('📊 ProfileHeader Debug:', {
        availableRoles: employee.roles?.map((r) => ({ id: r.id, title: r.title })),
        selectedPrimaryRole: primaryRole ? { id: primaryRole.id, title: primaryRole.title } : null,
        focusPower: focusPower ? { id: focusPower.id, title: focusPower.title, level: focusPower.level } : null,
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                p: 2,
                textAlign: 'center',
                position: 'relative',
            }}
        >
            <Box sx={{ position: 'relative' }}>
                {employee.pictureId ? (
                    <EmployeePicture
                        avatarSize={isMobile ? 140 : 100}
                        employee={employee}
                        allowUpload={itsMe ? true : undefined}
                    />
                ) : (
                    <Avatar
                        sx={{
                            width: isMobile ? 140 : 100,
                            height: isMobile ? 140 : 100,
                            fontSize: isMobile ? '1rem' : '1.5rem',
                            fontWeight: 400,
                            bgcolor: theme.palette.primary.main,
                            border: `2px solid ${theme.palette.secondary.main}`,
                        }}
                    >
                        {employee.firstName.charAt(0)}
                        {employee.lastName.charAt(0)}
                    </Avatar>
                )}
            </Box>

            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0, position: 'relative' }}>
                <Typography
                    variant={isMobile ? 'h1' : 'h2'}
                    sx={{
                        fontWeight: 500,
                        mb: 1,
                        wordBreak: 'break-word',
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                    }}
                >
                    {`${employee.firstName} ${employee.lastName}`}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 2.5,
                    }}
                >
                    {employeeOU && (
                        <Chip
                            icon={<Building2 size={14} />}
                            label={employeeOU.title}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                px: 1,
                                '& .MuiChip-icon': {
                                    color: theme.palette.primary.main,
                                },
                            }}
                        />
                    )}
                    {/* TODO: Backend - Add employee.location and company.defaultLocation fields */}
                    {/* <Chip
                        icon={<MapPin size={14} />}
                        label={ 'Berlin'}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            px: 1,
                            '& .MuiChip-icon': {
                                color: theme.palette.primary.main,
                            },
                        }}
                    /> */}
                    {focusPower && (
                        <Chip
                            icon={<Sparkles size={14} />}
                            label={focusPower.title}
                            size="small"
                            sx={{
                                bgcolor: theme.palette.accent?.main || theme.palette.secondary.main,
                                color: 'white',
                                fontWeight: 600,
                                px: 1,
                                '& .MuiChip-icon': {
                                    color: 'white',
                                },
                            }}
                        />
                    )}
                    {employee.availability && (
                        <Chip
                            icon={<Calendar size={14} />}
                            label={`${employee.availability.availability}%`}
                            size="small"
                            sx={{
                                bgcolor: theme.palette.success.main,
                                color: theme.palette.success.contrastText,
                                fontWeight: 500,
                                px: 1,
                                '& .MuiChip-icon': {
                                    color: theme.palette.success.contrastText,
                                },
                            }}
                        />
                    )}
                </Box>

                {/* Email */}
                {employee.email && (
                    <Box
                        component="a"
                        href={`mailto:${employee.email}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            mb: 1,
                            textDecoration: 'none',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                color: theme.palette.primary.main,
                            },
                        }}
                    >
                        <Mail size={16} />
                        <Typography variant="body2">{employee.email}</Typography>
                    </Box>
                )}

                {/* Social Media Icons */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                    {employee.linkedInUrl && (
                        <IconButton
                            size="small"
                            onClick={() => window.open(employee.linkedInUrl, '_blank')}
                            sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                },
                            }}
                        >
                            <Linkedin size={16} />
                        </IconButton>
                    )}
                    {employee.websiteUrl && (
                        <IconButton
                            size="small"
                            onClick={() => window.open(employee.websiteUrl, '_blank')}
                            sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                },
                            }}
                        >
                            <Globe size={16} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1 }}>
                {isTabletOrMobile ? (
                    <IconButton
                        size="small"
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                        }}
                    >
                        <MoreVertical size={16} />
                    </IconButton>
                ) : (
                    <>
                        {featureFlags.messages && (
                            <IconButton
                                size="small"
                                sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                }}
                            >
                                {getAppIcon(EIconCategory.T4LO_TALKS)}
                            </IconButton>
                        )}
                    </>
                )}
            </Box>

            {/* Mobile Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {featureFlags.messages && (
                    <MenuItem onClick={() => setMenuAnchor(null)}>
                        <ListItemIcon>{getAppIcon(EIconCategory.T4LO_TALKS)}</ListItemIcon>
                        <ListItemText>Nachricht senden</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
}

export default ProfileHeader;
export type { ProfileHeaderProps };
